// Small shared UI primitives. Styles live in src/styles/global.css under the
// same class names, so feature modules can reuse them without new CSS.
import type { CSSProperties, ReactNode } from 'react';

export function Card({ title, children, className = '', style }: { title?: string; children: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <section className={`card ${className}`} style={style}>
      {title ? <h3 className="section-label">{title}</h3> : null}
      {children}
    </section>
  );
}

export function Pill({ children, color, outlined = false, title }: { children: ReactNode; color: string; outlined?: boolean; title?: string }) {
  return (
    <span className={`pill ${outlined ? 'pill-outlined' : ''}`} style={{ '--c': color } as CSSProperties} title={title}>
      {children}
    </span>
  );
}

export function MetaRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="meta-row">
      <span className="meta-label">{label}</span>
      <span className="meta-val">{value}</span>
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="status" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      {label ? <div>{label}</div> : null}
    </div>
  );
}

export function StatusMessage({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="status">
      <div>{children}</div>
      {action ? <div className="status-action">{action}</div> : null}
    </div>
  );
}

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  ariaLabel,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'ghost' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <button type={type} className={`btn btn-${variant} ${className}`} onClick={onClick} disabled={disabled} aria-label={ariaLabel}>
      {children}
    </button>
  );
}

export function IconButton({
  children,
  onClick,
  label,
  active,
  disabled,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  label: string;
  active?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`icon-btn ${active ? 'is-active' : ''} ${className}`}
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function Switch({ checked, onChange, color, label }: { checked: boolean; onChange: (on: boolean) => void; color?: string; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`switch ${checked ? 'on' : ''}`}
      style={color ? ({ '--c': color } as CSSProperties) : undefined}
      onClick={() => onChange(!checked)}
    >
      <span className="switch-knob" />
    </button>
  );
}

export function Segmented<T extends string>({ options, value, onChange, label }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`segmented-item ${o.value === value ? 'is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function SectionHeader({ children }: { children: ReactNode }) {
  return <div className="list-section-header">{children}</div>;
}
